import type { BuildOp, C2S, S2C, HeroId, InputState } from '@knight/shared';
import { WsTransport, LocalTransport, type Transport } from '../net/transport';
import { api, getToken } from '../net/api';
import { GameView } from './view';
import { Input } from './input';
import { audio } from './audio';
import { t, tn } from '../i18n';
import * as S from '../app/state';
import { prefs, setPref } from '../app/prefs';
import { hintOnce } from '../app/hints';
import { isTouchUi, keepAwake } from '../app/device';
import { RARITY_COLORS } from './sprites';

/** Owns the connection, the Pixi view and the 20 Hz input loop. */
class GameClient {
  transport: Transport | null = null;
  view: GameView | null = null;
  input = new Input();
  myId = 0;
  joined = false;
  private seq = 0;
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pingSent = new Map<number, number>();
  private pingN = 0;
  private viewContainer: HTMLElement | null = null;
  private pendingJoin: C2S | null = null;
  private lastSelfPush = 0;
  private leaveTimer: ReturnType<typeof setTimeout> | null = null;
  /** the hero the player asked for; the room may resume a saved one instead */
  private wantHero: HeroId | null = null;
  private lastAim: { x: number; y: number } | null = null;
  private mode: 'realm' | 'duel' = 'realm';
  private bossAlive = false;
  /** last welcome, re-applied when the view mounts after the message arrived */
  private welcome: Extract<S2C, { t: 'welcome' }> | null = null;
  /** static unit fields seen before a view existed */
  private earlyStatics = new Map<number, Partial<import('@knight/shared').UnitView>>();

  private applyWelcomeToView(): void {
    const w = this.welcome;
    if (!w || !this.view) return;
    this.view.myId = w.id;
    this.view.myTeam = w.team;
    this.view.setMap(w.map);
    this.view.clearEntities();
    this.view.serverTick = w.tick;
    this.view.self = null;
    for (const [id, st] of this.earlyStatics) this.view.staticBacklog.set(id, st);
    this.earlyStatics.clear();
  }

  async connect(): Promise<void> {
    this.disconnect();
    S.conn.value = 'connecting';
    let online = false;
    const forceOffline = new URLSearchParams(location.search).has('offline');
    try {
      if (forceOffline) throw new Error('forced offline');
      const h = await api.health();
      online = !!h.ok;
      S.googleReady.value = !!h.google;
    } catch {
      online = false;
    }
    if (online) {
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WsTransport(`${proto}//${location.host}/ws`);
      this.transport = ws;
      S.offlineMode.value = false;
      ws.onOpen = () => {
        const token = getToken();
        if (token) ws.send({ t: 'hello', token, version: 1 });
        S.conn.value = 'online';
        if (this.pendingJoin) {
          ws.send(this.pendingJoin);
          this.pendingJoin = null;
        }
      };
      ws.onMessage = (m) => this.handle(m);
      ws.onClose = () => {
        if (this.transport === ws) {
          S.conn.value = 'error';
          this.joined = false;
          if (S.inGame.value) this.exitGame();
        }
      };
    } else {
      const acc = S.account.value;
      const lt = new LocalTransport(acc?.id ?? 'local', acc?.name ?? 'Wanderer');
      this.transport = lt;
      lt.onMessage = (m) => this.handle(m);
      lt.onClose = () => {};
      S.offlineMode.value = true;
      S.conn.value = 'offline';
    }
    this.pingTimer = setInterval(() => this.ping(), 2000);
  }

  disconnect(): void {
    this.transport?.close();
    this.transport = null;
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
    this.joined = false;
  }

  rehello(): void {
    const token = getToken();
    if (token && this.transport?.kind === 'ws') this.transport.send({ t: 'hello', token, version: 1 });
  }

  send(m: C2S): void {
    this.transport?.send(m);
  }

  private ping(): void {
    if (!this.transport) return;
    const n = ++this.pingN;
    this.pingSent.set(n, performance.now());
    this.send({ t: 'ping', n });
    if (this.pingSent.size > 10) this.pingSent.delete(n - 10);
  }

  // ------------------------------------------------------------------ view lifecycle

  async mountView(container: HTMLElement): Promise<void> {
    if (this.view) return;
    this.viewContainer = container;
    const view = new GameView();
    this.view = view;
    view.onSfx = (k, cue) => audio.sfx(k, cue);
    this.input.setTouch(isTouchUi());
    // a phone screen is small: the map starts hidden and the ⌖ button brings it back
    view.showMinimap = !isTouchUi();
    this.input.onStick = (kind, s) => {
      if (kind === 'move') S.stickMove.value = s;
      else S.stickAim.value = s;
    };
    view.onListener = (x, y, threat, tier) => {
      audio.setListener(x, y);
      audio.setTier(tier);
      if (this.mode !== 'duel') audio.setIntensity(this.bossAlive ? Math.max(1, threat) : threat);
    };
    await view.init(container);
    if (this.view !== view) return;
    this.applyWelcomeToView();
    this.input.attach(container);
    // a thumb cannot hold an attack button, so touch play attacks on its own unless told otherwise
    this.input.autoFire = isTouchUi() ? prefs.value.autoFireTouch : prefs.value.autoFire;
    this.input.onToggle = (what) => {
      if (what === 'build') S.buildOpen.value = !S.buildOpen.value;
      if (what === 'chat') S.chatOpen.value = !S.chatOpen.value;
      if (what === 'menu') S.menuOpen.value = !S.menuOpen.value;
      if (what === 'map') view.showMinimap = !view.showMinimap;
      if (what === 'autofire') {
        setPref(isTouchUi() ? 'autoFireTouch' : 'autoFire', this.input.autoFire);
        S.showToast(t('set.autofire') + ': ' + (this.input.autoFire ? 'on' : 'off'));
      }
      if (what === 'leave') this.toggleLeave();
    };
    if (this.tickTimer) clearInterval(this.tickTimer);
    this.tickTimer = setInterval(() => this.tick(), 50);
    keepAwake(true);
  }

  unmountView(): void {
    keepAwake(false);
    if (this.tickTimer) clearInterval(this.tickTimer);
    this.tickTimer = null;
    this.input.detach();
    this.view?.destroy();
    this.view = null;
    if (this.viewContainer) this.viewContainer.innerHTML = '';
  }

  private tick(): void {
    if (!this.joined || !this.view || !this.transport) return;
    // spectators only watch: the camera follows the hero, nothing is sent
    if (S.spectate.value) {
      this.input.consume();
      return;
    }
    const me = S.self.value;
    if (me?.dead) return;
    const ti = this.input.consume();
    const aim = this.resolveAim(ti);
    this.view.aimAngle = Math.atan2(aim.y - this.view.pred.y, aim.x - this.view.pred.x);
    const seq = ++this.seq;
    this.view.predict(seq, ti.mx, ti.my, ti.dash);
    const i: InputState = { mx: ti.mx, my: ti.my, ax: Math.round(aim.x), ay: Math.round(aim.y), fire: ti.fire, skills: ti.skills, dash: ti.dash, use: ti.use, pick: ti.pick, interact: ti.interact, seq };
    this.send({ t: 'input', i });
  }

  /**
   * Where the hero shoots. A mouse points at the cursor; a thumb either pushes the right stick,
   * or lets the hero pick the nearest enemy, or fires the way it walks.
   */
  private resolveAim(ti: import('./input').TickInput): { x: number; y: number } {
    const view = this.view!;
    const p = view.pred;
    const REACH = 260;
    if (ti.aimDx || ti.aimDy) {
      this.lastAim = { x: p.x + ti.aimDx * REACH, y: p.y + ti.aimDy * REACH };
      return this.lastAim;
    }
    if (this.input.touch) {
      const foe = view.nearestEnemyPos();
      if (foe) {
        this.lastAim = foe;
        return foe;
      }
      if (ti.mx || ti.my) {
        const len = Math.hypot(ti.mx, ti.my) || 1;
        this.lastAim = { x: p.x + (ti.mx / len) * REACH, y: p.y + (ti.my / len) * REACH };
        return this.lastAim;
      }
      return this.lastAim ?? { x: p.x + REACH, y: p.y };
    }
    const w = view.screenToWorld(ti.aimScreenX, ti.aimScreenY);
    this.lastAim = w;
    return w;
  }

  // ------------------------------------------------------------------ actions

  join(mode: 'realm' | 'duel', heroId: HeroId, code?: string, ghost?: string, slot?: number): void {
    const msg: C2S = { t: 'join', mode, heroId, code, ghost, slot: mode === 'realm' ? slot ?? S.slotChoice.value : undefined };
    this.wantHero = heroId;
    S.resetGameState();
    S.gameMode.value = mode;
    this.mode = mode;
    if (this.transport?.kind === 'ws' && S.conn.value !== 'online') this.pendingJoin = msg;
    else this.send(msg);
    S.inGame.value = true;
    S.screen.value = 'game';
    audio.unlock();
  }

  respawn(heroId: HeroId): void {
    S.deathCard.value = null;
    this.send({ t: 'respawn', heroId });
  }

  /**
   * Asks to leave. A living hero in a realm channels for a second first, so the exit cannot
   * dodge a killing blow; the room answers with 'leaving' and then 'left'.
   */
  leave(): void {
    if (S.spectate.value) {
      this.unwatch();
      return;
    }
    this.send({ t: 'leave' });
    S.menuOpen.value = false;
    // a lost answer must never trap the player in a realm
    if (this.leaveTimer) clearTimeout(this.leaveTimer);
    this.leaveTimer = setTimeout(() => {
      if (S.inGame.value) this.exitGame();
    }, 6000);
  }

  cancelLeave(): void {
    if (!S.leaving.value) return;
    S.leaving.value = null;
    if (this.leaveTimer) clearTimeout(this.leaveTimer);
    this.leaveTimer = null;
    this.send({ t: 'leaveCancel' });
  }

  toggleLeave(): void {
    if (S.spectate.value) {
      this.unwatch();
      return;
    }
    if (S.leaving.value) this.cancelLeave();
    else this.leave();
  }

  exitGame(): void {
    this.joined = false;
    this.wantHero = null;
    this.lastAim = null;
    S.queue.value = 'idle';
    S.inGame.value = false;
    S.screen.value = S.squad.value ? 'squad' : 'home';
    S.resetGameState();
    this.unmountView();
    audio.music('menu');
    void this.refreshAccount();
  }

  /** Re-reads the account after a run so saved heroes, fame and pets are current. */
  async refreshAccount(): Promise<void> {
    if (S.conn.value !== 'online') {
      S.heroesTick.value++;
      return;
    }
    try {
      S.account.value = await api.me();
    } catch {
      /* keep the stale view */
    }
  }

  chat(text: string): void {
    const tx = text.trim();
    if (!tx) return;
    this.send({ t: 'chat', text: tx.slice(0, 200) });
  }

  /** Re-reads the control mode after the settings change it. */
  applyControlMode(): void {
    this.input.setTouch(isTouchUi());
    if (this.view) this.view.showMinimap = !isTouchUi();
  }

  /** Rank spending, gear moves and auto-cast toggles; the server answers with a build message. */
  build(op: BuildOp): void {
    this.send({ t: 'build', op });
  }

  /** Show mode: follow another player's hero without a hero of your own. */
  watch(name: string): void {
    if (S.conn.value !== 'online') {
      S.showToast(t('toast.needServer'));
      return;
    }
    S.resetGameState();
    S.gameMode.value = 'realm';
    this.mode = 'realm';
    this.send({ t: 'watch', name });
    S.inGame.value = true;
    S.screen.value = 'game';
    audio.unlock();
  }

  unwatch(): void {
    this.send({ t: 'unwatch' });
    this.exitGame();
  }

  crowd(id: string): void {
    this.send({ t: 'crowd', id });
  }

  // ------------------------------------------------------------------ messages

  handle(m: S2C): void {
    switch (m.t) {
      case 'welcome': {
        this.myId = m.id;
        this.joined = true;
        this.mode = m.mode;
        S.gameMode.value = m.mode;
        S.inGame.value = true;
        S.screen.value = 'game';
        S.deathCard.value = null;
        S.queue.value = 'idle';
        this.welcome = m;
        this.applyWelcomeToView();
        S.spectate.value = m.spectate ?? null;
        if (!m.spectate) S.crowdPrices.value = null;
        if (m.mode === 'realm') S.realm.value = { code: m.code, phase: 'open', corruption: 0, players: 1, boss: '', closeIn: 0 };
        else S.duel.value = { code: m.code, phase: 'waiting', round: 1, wins: [0, 0], names: [], until: 0 };
        this.bossAlive = false;
        audio.music(m.mode === 'duel' ? 'duel' : 'realm');
        if (m.mode === 'duel') audio.setIntensity(1);
        break;
      }
      case 'snap': {
        if (!this.view || !this.view.map) {
          // keep static fields so units are not nameless once the view is ready
          const backlog = this.view?.staticBacklog ?? this.earlyStatics;
          for (const u of m.s.u) if (u.n !== undefined || u.d !== undefined || u.h !== undefined) backlog.set(u.id, { n: u.n, d: u.d, h: u.h, el: u.el, sz: u.sz });
          break;
        }
        const firstSelf = !this.view.self && m.s.me;
        this.view.applySnapshot(m.s);
        if (firstSelf && m.s.me) this.view.resetSelf(m.s.me.x, m.s.me.y);
        if (m.s.me) {
          if (this.wantHero && !S.spectate.value) {
            const got = m.s.me.hero;
            if (got !== this.wantHero) S.showToast(t('toast.heroResumed', { name: tn('hero', got) }));
            this.wantHero = null;
          }
          if (m.s.me.draft && !S.self.value?.draft && !S.spectate.value) hintOnce('draft', isTouchUi() ? t('hint.draftTouch') : t('hint.draft'));
          const now = performance.now();
          if (now - this.lastSelfPush > 90 || m.s.me.draft !== S.self.value?.draft || m.s.me.dead !== S.self.value?.dead) {
            this.lastSelfPush = now;
            S.self.value = m.s.me;
          }
        }
        // boss bar
        let boss: { name: string; hp: number; mhp: number } | null = null;
        for (const u of this.view.units.values()) {
          if (u.view.k === 2) {
            boss = { name: tn('mob', u.view.d ?? ''), hp: u.view.hp, mhp: u.view.mhp };
            break;
          }
        }
        if ((boss && !S.bossBar.value) || (!boss && S.bossBar.value) || (boss && S.bossBar.value && (boss.hp !== S.bossBar.value.hp || boss.mhp !== S.bossBar.value.mhp))) S.bossBar.value = boss;
        break;
      }
      case 'ev': {
        this.view?.applyEvents(m.e);
        for (const e of m.e) {
          if (e.t === 'announce') {
            const vars = { ...(e.vars ?? {}) };
            if (typeof vars.boss === 'string') vars.boss = tn('mob', vars.boss);
            S.showAnnounce(t(e.key, vars), e.color);
            if (e.key === 'announce.bossArrives') this.bossAlive = true;
            if (e.key === 'announce.bossSlain') this.bossAlive = false;
          } else if (e.t === 'feed') {
            const vars = { ...e.vars };
            if (typeof vars.boss === 'string') vars.boss = tn('mob', vars.boss);
            if (typeof vars.item === 'string') vars.item = tn('item', vars.item);
            if (typeof vars.effect === 'string') vars.effect = tn('crowd', vars.effect);
            const color = e.key.includes('legendary') || e.key.includes('relic') ? '#ffb347' : e.key.includes('crowd') ? '#ff9ae8' : e.key.includes('revenant') ? '#c8b8ff' : e.key.includes('boss') ? '#ffd166' : undefined;
            S.pushFeed(t(e.key, vars), color);
          } else if (e.t === 'rankup') {
            if (e.id === this.myId) {
              S.showAnnounce(t('announce.rankUp', { rank: e.rank }), '#ffd166');
              audio.sfx('levelup');
              if (!S.spectate.value) hintOnce('rank', t('hint.rank'));
            }
          } else if (e.t === 'pickup') {
            if (e.id === this.myId && e.type === 'item' && e.what) {
              S.pushFeed(t('feed.pickedItem', { item: tn('item', e.what) }), RARITY_COLORS[e.amount ?? 0] ?? '#cfc7b8');
              if (!S.spectate.value) hintOnce('bag', isTouchUi() ? t('hint.bagTouch') : t('hint.bag', { key: 'Tab' }));
            }
          } else if (e.t === 'crowd') {
            if (e.id === this.myId) S.showAnnounce(t(e.kind === 'harm' ? 'announce.crowdHarm' : 'announce.crowdHelp', { by: e.by, effect: tn('crowd', e.effect) }), e.kind === 'harm' ? '#ff6a8a' : '#8fe38a');
          }
        }
        break;
      }
      case 'realm':
        S.realm.value = { code: S.realm.value?.code ?? '', phase: m.phase, corruption: m.corruption, players: m.players, boss: m.boss, closeIn: m.closeIn };
        break;
      case 'duel':
        S.duel.value = { code: S.duel.value?.code ?? '', phase: m.phase, round: m.round, wins: m.wins, names: m.names, until: m.until, winner: m.winner };
        audio.setIntensity(m.phase === 'fight' ? 2 : 1);
        break;
      case 'death':
        S.deathCard.value = m.card;
        S.self.value = S.self.value ? { ...S.self.value, dead: true } : S.self.value;
        void api.event('death', { level: m.card.level, hero: m.card.heroId, killer: m.card.killer.kind });
        break;
      case 'chat':
        S.pushChat(m.from, m.text, m.system, m.spectator);
        if (m.id !== undefined && !m.system) this.view?.showBubble(m.id, m.text);
        break;
      case 'spectate':
        if (m.target) S.spectate.value = m.target;
        else if (S.spectate.value) {
          S.showToast(t(m.reason === 'closed' ? 'toast.watchClosed' : 'toast.watchEnded'));
          this.exitGame();
        }
        break;
      case 'build':
        if (!m.ok && m.err) S.showToast(t(m.err));
        break;
      case 'crowd':
        if (m.prices) S.crowdPrices.value = m.prices;
        if (m.ok) {
          S.crowdLastBuy.value = performance.now();
          void this.refreshAccount();
        } else if (m.err) S.showToast(t(m.err));
        break;
      case 'squad':
        S.squad.value = { code: m.code, members: m.members };
        if (!S.inGame.value) S.screen.value = 'squad';
        break;
      case 'squad.none':
        S.squad.value = null;
        break;
      case 'queue':
        S.queue.value = m.state === 'cancelled' ? 'idle' : m.state;
        break;
      case 'leaving':
        if (m.cancelled) {
          S.leaving.value = null;
          if (this.leaveTimer) clearTimeout(this.leaveTimer);
          this.leaveTimer = null;
          if (m.reason) S.showToast(t(m.reason));
        } else S.leaving.value = { sec: m.sec, at: performance.now() };
        break;
      case 'left':
        if (this.leaveTimer) clearTimeout(this.leaveTimer);
        this.leaveTimer = null;
        this.exitGame();
        break;
      case 'admin': {
        const cur = S.admin.value;
        S.admin.value = { enabled: m.enabled ?? cur.enabled, players: m.data?.players ?? cur.players, realm: m.data?.realm ?? cur.realm };
        if (m.data?.teleport && this.view) this.view.resetSelf(m.data.teleport.x, m.data.teleport.y);
        if (m.msg) S.showToast(m.msg);
        if (m.ok && m.data?.account) void this.refreshAccount();
        break;
      }
      case 'error':
        if (m.key === 'auth') {
          // token rejected: caller re-authenticates
          S.showToast(t('toast.error', { msg: m.msg ?? m.key }));
          this.onAuthError?.();
        } else S.showToast(t(m.key) !== m.key ? t(m.key) : t('toast.error', { msg: m.msg ?? m.key }));
        break;
      case 'kicked':
        S.showToast(m.reason);
        this.exitGame();
        break;
      case 'notice':
        S.showToast(t(m.key, m.vars));
        break;
      case 'pong': {
        const sent = this.pingSent.get(m.n);
        if (sent) S.ping.value = Math.round(performance.now() - sent);
        break;
      }
    }
  }

  onAuthError: (() => void) | null = null;
}

export const game = new GameClient();
